// Updated App.jsx - Fixed routing with Always-Active Homepage and PWA Install Prompt
import React, { useEffect, useState, useContext, createContext } from 'react';
import { motion } from 'framer-motion';
import { PrivacyAuthProvider, usePrivacyAuth } from './context/PrivacyAuthContext';
// VibeGamificationProvider causes circular dependency - Navigation handles missing context gracefully
import { AppProvider, useApp } from './context/AppContext';
import { UserProvider } from './context/UserContext';
import { useContextIntegration } from './hooks/useContextIntegration';
import Navigation from './components/shared/Navigation';
import Footer from './components/shared/Footer';
import StandaloneSignup from './components/auth/StandaloneSignup';

// Local SkipLink component for accessibility
const SkipLink = ({ href = '#main-content' }) => (
  <a
    href={href}
    className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-purple-500 focus:text-white focus:rounded-lg"
  >
    Skip to main content
  </a>
);
// Import critical pages directly (not lazy) to avoid loading issues
import MindEaseHomePage from './pages/MindEaseHomePage';
import FriendshipModulesPage from './pages/FriendshipModulesPage'; // Import directly - critical page

// Lazy load less critical pages for better performance
const LazyMindEaseDashboard = React.lazy(() => import('./pages/MindEaseDashboard'));
// Assessment components
const LazyVibeAssessmentIntro = React.lazy(() => import('./components/assessment/VibeAssessmentIntro'));
const LazyVibeAssessment = React.lazy(() => import('./components/assessment/VibeAssessment'));
const LazyVibeAssessmentResultsView = React.lazy(() => import('./components/assessment/VibeAssessmentResultsView'));
const LazyVibeToolsPage = React.lazy(() => import('./pages/VibeToolsPage'));
const LazyVibeResourcesPage = React.lazy(() => import('./pages/VibeResourcesPage'));
const LazyVibeDataPrivacyPage = React.lazy(() => import('./pages/VibeDataPrivacyPage'));
const LazyVibeLifeDesign = React.lazy(() => import('./pages/VibeLifeDesign'));
const LazyCoachingPage = React.lazy(() => import('./pages/CoachingPage'));
const LazyCoachingPrivacyPage = React.lazy(() => import('./pages/CoachingPrivacyPage'));
const LazyPrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ErrorBoundary from './components/errorBoundary/ErrorBoundary';
import errorTrackingService from './services/errorTracking/ErrorTrackingService';
import VibeLogin from './components/auth/VibeLogin';

// Loading fallback component for lazy loading
const LoadingFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
    <div className="text-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full mx-auto mb-4"
      />
      <div className="text-white text-lg font-semibold">MindEase Vibe</div>
      <div className="text-purple-300 text-sm mt-2">Loading your tools…</div>
    </div>
  </div>
);

// Context Integration Component
const ContextIntegration = ({ children }) => {
  useContextIntegration(); // This connects AppContext with VibeGamificationContext
  
  return children;
};

const AppContent = () => {
  const { user, isLoading } = usePrivacyAuth();
  const { currentView, assessmentResults, setCurrentView, scrollToTop } = useApp();

  // Pull any tool entries and Vibe Check results saved on the auth hub down
  // into this browser.
  //
  // Deliberately fire-and-forget and deliberately NOT awaited: the hub is
  // sync, not storage, and the app is local-first. If this never resolves -
  // offline, hub down, no session - the tools still open instantly on
  // whatever is in this browser. The idle callback keeps it off the first
  // paint entirely.
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      import('./services/ToolSyncService')
        .then((m) => m.hydrateFromHub())
        .catch(() => { /* local-first: a failed sync is not a failed app */ });
    };
    const id = typeof window !== 'undefined' && window.requestIdleCallback
      ? window.requestIdleCallback(run, { timeout: 4000 })
      : setTimeout(run, 2000);
    return () => {
      cancelled = true;
      if (typeof window !== 'undefined' && window.cancelIdleCallback) {
        try { window.cancelIdleCallback(id); } catch { /* setTimeout id */ }
      }
    };
  }, []);

  // DEBUGGING: Log whenever user state changes
  useEffect(() => {
    console.log('👤 App.jsx: User state changed:', user);
    console.log('👤 Loading state:', isLoading);

    // Also log localStorage state for comparison
    const cachedUser = localStorage.getItem('vibe_user_identity');
    const authSuccess = localStorage.getItem('vibe_auth_success');
    const userId = localStorage.getItem('vibe_user_id');
    console.log('👤 localStorage state:');
    console.log('  - vibe_user_identity:', cachedUser);
    console.log('  - vibe_auth_success:', authSuccess);
    console.log('  - vibe_user_id:', userId);
  }, [user, isLoading]);

  // Scroll to top whenever the view changes
  useEffect(() => {
    scrollToTop('smooth');
  }, [currentView, scrollToTop]);

  // Handle legacy 'progress' redirect - must use useEffect to avoid setState during render
  useEffect(() => {
    if (currentView === 'progress') {
      setCurrentView('dashboard');
    }
  }, [currentView, setCurrentView]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.view) {
        setCurrentView(event.state.view);
      } else if (event.state && event.state.previousView) {
        setCurrentView(event.state.previousView);
      } else {
        // Default to modules view if no state
        setCurrentView('modules');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [setCurrentView]);

  // Views that don't require authentication (guest-friendly)
  const _guestAllowedViews = [
    'home', 
    'dashboard', // Now guest-friendly with limited features
    'vibe-assessment-intro',
    'vibe-assessment', 
    'resources',
    'data-privacy',
  ];

  // Views that require authentication - now only dashboard and data-privacy require auth
  const authRequiredViews = [
    'progress', // Legacy redirect to dashboard
    'life-design-hub', // New view placeholder
  ];

  const requiresAuth = authRequiredViews.includes(currentView);

  // Handle assessment completion and post-signup flow
  // The Vibe Check commits its own result the moment it is calculated (see
  // VibeAssessment); Continue only moves on. Re-committing here would award
  // the XP twice and append a second history entry.
  const handleAssessmentComplete = () => {
    setCurrentView('dashboard');
  };

  // Handle auth-required navigation
  const handleAuthRequiredNavigation = (_view) => {
    if (!user && requiresAuth) {
      // Redirect to pure HTML signup page
      window.location.href = '/signup.html';
      return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white">Redirecting to signup...</div>
      </div>;
    }
    return null;
  };

  // Loading screen with Vibe styling - with timeout failsafe
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);

  React.useEffect(() => {
    if (isLoading) {
      console.log('⏰ Loading screen shown, setting 10s timeout');
      const timer = setTimeout(() => {
        console.error('❌ Loading timeout reached! Force continuing...');
        setLoadingTimeout(true);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  if (isLoading && !loadingTimeout) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">🤝</span>
            </div>
          </motion.div>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"
          />
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">MindEase Vibe</h2>
          <p className="text-purple-200 text-sm sm:text-base">Getting things ready…</p>
        </div>
      </div>
    );
  }

  // If loading timed out, force render anyway with error boundary
  if (loadingTimeout) {
    console.warn('⚠️ Loading timed out, forcing render with current state');
  }

  // Check if trying to access auth-required content as guest
  const authCheck = handleAuthRequiredNavigation(currentView);
  if (authCheck) return authCheck;
  
  
  const renderCurrentView = () => {
    console.log('🎯 App.jsx renderCurrentView called with:', currentView);
    console.log('🎯 User:', user);
    console.log('🎯 isLoading:', isLoading);

    // Handle all views with proper routing logic
    switch (currentView) {
      case 'dashboard':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyMindEaseDashboard />
          </React.Suspense>
        );
        
      case 'vibe-assessment-intro':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeAssessmentIntro />
          </React.Suspense>
        );

      case 'vibe-assessment':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeAssessment onComplete={handleAssessmentComplete} previousResults={assessmentResults} />
          </React.Suspense>
        );

      case 'vibe-assessment-results':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeAssessmentResultsView />
          </React.Suspense>
        );
        
      case 'modules':
        return <FriendshipModulesPage />;

      case 'progress':
        // Legacy redirect handled in useEffect above - just show dashboard
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyMindEaseDashboard />
          </React.Suspense>
        );
        
      case 'tools':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeToolsPage />
          </React.Suspense>
        );

      case 'life-design':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeLifeDesign />
          </React.Suspense>
        );

      case 'life-design-landing':
        // For guests accessing life design
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeLifeDesign />
          </React.Suspense>
        );
        
      case 'data-privacy':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeDataPrivacyPage />
          </React.Suspense>
        );
        
      case 'coaching':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyCoachingPage />
          </React.Suspense>
        );
      case 'privacy-policy':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyPrivacyPolicyPage />
          </React.Suspense>
        );

      case 'coaching-privacy':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyCoachingPrivacyPage />
          </React.Suspense>
        );

      case 'resources':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeResourcesPage />
          </React.Suspense>
        );
        
      case 'life-design-hub':
        // Placeholder for new Conscious Life Design modules
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-32">
            <div className="container mx-auto px-4 py-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-2xl mx-auto"
              >
                <div className="text-6xl mb-6">🔮</div>
                <h1 className="text-4xl font-bold text-white mb-4">
                  Conscious Life Design Hub
                </h1>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                  <p className="text-xl text-purple-200 mb-6">
                    Your gateway to intentional life creation and future self work.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <h3 className="font-semibold text-white mb-2">Advanced Modules</h3>
                      <p className="text-sm text-slate-300">Life design, identity work, and future planning</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <h3 className="font-semibold text-white mb-2">Interactive Tools</h3>
                      <p className="text-sm text-slate-300">Vision boards, goal mapping, and reflection exercises</p>
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-yellow-400 text-black rounded-full inline-block font-semibold">
                    🚧 Coming Soon - Under Development 🚧
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        );
        
      
      
      case 'home':
      default:
        // The same home page for guests and signed-in readers.
        return <MindEaseHomePage />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SkipLink href="#main-content" />
      <Navigation />

      {/* Temporarily disable all AnimatePresence to isolate the useEffect issue */}
      <main id="main-content" tabIndex="-1" className="flex-1">
        {renderCurrentView()}
      </main>

      {/* Global Footer */}
      <Footer />

      {/* ADD PWA INSTALL PROMPT HERE - Shows globally across all views */}
      <PWAInstallPrompt />
    </div>
  );
};

// Create a simple context for navigation
const NavigationContext = createContext();

// Hook to trigger signup from navigation
window.triggerSignup = () => {
  window.location.href = `${window.location.origin }?signup=true`;
  window.location.reload();
};

// Top-level router before any context
const TopLevelRouter = () => {
  const [currentView, setCurrentView] = useState('home');

  useEffect(() => {
    // Check URL parameters for initial view
    const urlParams = new URLSearchParams(window.location.search);
    console.log('🔍 TopLevelRouter: URL params:', window.location.search);
    console.log('🔍 TopLevelRouter: signup param:', urlParams.get('signup'));
    console.log('🔍 TopLevelRouter: view param:', urlParams.get('view'));

    // Check for signup mode
    if (urlParams.get('signup') === 'true') {
      console.log('🎯 TopLevelRouter: Setting view to signup');
      setCurrentView('signup');
    }
    // Check for view parameter
    else if (urlParams.get('view')) {
      const viewParam = urlParams.get('view');
      console.log('🎯 TopLevelRouter: Setting view to:', viewParam);
      setCurrentView(viewParam);
    }
  }, []);
  
  // Handle signup completely outside of any context
  if (currentView === 'signup') {
    console.log('🚀 TopLevelRouter: Rendering StandaloneSignup - NO CONTEXTS ACTIVE');
    return (
      <StandaloneSignup 
        onComplete={() => {
          // Navigate to dashboard after signup
          window.location.href = window.location.origin;
        }}
        onSkip={() => {
          // Navigate to home as guest
          window.location.href = window.location.origin;
        }}
        onBack={() => {
          // Navigate back to home
          window.location.href = window.location.origin;
        }}
      />
    );
  }
  
  // For all other views, use the full app with contexts
  return (
    <NavigationContext.Provider value={{ currentView, setCurrentView }}>
      <ErrorBoundary name="App">
        <PrivacyAuthProvider>
          <AppProvider>
            <AppRoutingWrapper />
          </AppProvider>
        </PrivacyAuthProvider>
      </ErrorBoundary>
    </NavigationContext.Provider>
  );
};

// Main App component with all providers
const App = () => {
  // Initialize error tracking and gamification
  useEffect(() => {
    // Set up error tracking context
    errorTrackingService.setContext({
      appVersion: '2.0.0',
      edition: 'vibe',
      environment: import.meta.env.MODE,
    });

    // Initialize Zustand gamification store
    import('./stores/gamificationStore').then(({ initializeGamification }) => {
      initializeGamification();
    });

    // Check if user logged in via HTML pages
    const authSuccess = localStorage.getItem('vibe_auth_success');
    const userId = localStorage.getItem('vibe_user_id');

    if (authSuccess === 'true' && userId) {
      console.log('User authenticated via HTML signup:', userId);
      // Clear the flags
      localStorage.removeItem('vibe_auth_success');

      // The PrivacyAuthContext should pick up the Firebase auth state
      // and update accordingly
    }
  }, []);

  return <TopLevelRouter />;
};

// Separate routing wrapper to handle signup isolation
const AppRoutingWrapper = () => {
  const { currentView, setCurrentView } = useContext(NavigationContext);
  // Override the app context currentView with our prop
  const appContext = useApp();
  useEffect(() => {
    appContext.setCurrentView(currentView);
  }, [currentView, appContext.setCurrentView]);
  
  
  // Handle login form isolated as well
  if (currentView === 'login') {
    return (
      <div className="min-h-screen">
        <Navigation />
        <VibeLogin 
          onComplete={() => setCurrentView('dashboard')} 
          onBackToSignup={() => setCurrentView('signup')} 
        />
        <PWAInstallPrompt />
      </div>
    );
  }
  
  // All other views get the full context
  // NOTE: VibeGamificationProvider removed - using Zustand instead (no provider needed!)
  return (
    <ErrorBoundary name="AppContent">
      <UserProvider>
        <ContextIntegration>
          <AppContent />
        </ContextIntegration>
      </UserProvider>
    </ErrorBoundary>
  );
};

export default App;